import { FC, useEffect, useRef, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import type {
  Map as LeafletMap,
  GeoJSON as LeafletGeoJSON,
  Layer,
  PathOptions,
} from 'leaflet';
import type { Feature, GeoJsonObject } from 'geojson';
import 'leaflet/dist/leaflet.css';
import styled from 'styled-components';
import tw from 'twin.macro';
import strings from '@/language/Swedish';
import {
  MapstatBoundsType,
  MapstatGeoJsonType,
} from '@/api/mutations/useMapstatSearch';
import { ColorStopType, getPolygonColor, DEFAULT_CENTER, DEFAULT_ZOOM } from './mapstatConfig';

const StyledMap = styled.div`
  ${tw`flex relative h-full w-full md:rounded-xl overflow-hidden bg-white`}

  .leaflet-container {
    ${tw`w-full h-full z-0`}
  }
`;

const OverlayCard = styled.div`
  ${tw`absolute z-[1001] rounded-lg border border-slate-400 bg-white/95 shadow-lg pointer-events-none`};
`;

const HoverCard = styled(OverlayCard)`
  ${tw`top-3 right-3 min-w-[280px] max-w-[440px] px-4 py-3`};
`;

const HoverTitle = styled.div`
  ${tw`text-[13px] font-semibold text-slate-800`};
`;

const HoverDivider = styled.div`
  ${tw`h-px bg-slate-200 my-2`};
`;

const HoverLabel = styled.div`
  ${tw`text-[12.5px] font-medium text-slate-700 mb-1`};
`;

const HoverValueRow = styled.div`
  ${tw`flex items-baseline gap-1.5`};
`;

const HoverSwatch = styled.i<{ $color: string }>`
  ${tw`w-2.5 h-2.5 flex-none self-center rounded-full border border-slate-800/20`};
  background-color: ${({ $color }) => $color};
`;

const HoverValue = styled.span`
  ${tw`text-[17px] font-semibold text-slate-900`};
`;

const HoverUnit = styled.span`
  ${tw`text-xs text-slate-500`};
`;

const LegendCard = styled(OverlayCard)`
  ${tw`left-3 bottom-8 px-3 py-2.5 text-xs text-slate-600`};
`;

const LegendHeading = styled.div`
  ${tw`font-semibold text-slate-800 mb-1`};
`;

const LegendRow = styled.div`
  ${tw`flex items-center gap-1.5 mt-0.5`};
`;

const LegendSwatch = styled.i<{ $color: string }>`
  ${tw`w-3.5 h-3.5 flex-none rounded border border-slate-800/20`};
  background-color: ${({ $color }) => $color};
`;

const SearchButton = styled.button`
  ${tw`absolute z-[1001] right-3 bottom-8 h-12 min-w-[220px] px-12 rounded-[10px] border-none bg-primary text-white text-[17px] font-medium shadow-lg cursor-pointer`};

  &:hover {
    ${tw`brightness-110`};
  }
`;

const FullscreenButton = styled.button`
  ${tw`absolute z-[1001] top-3 left-3 w-10 h-10 rounded-lg border border-slate-400 bg-white/95 text-slate-700 text-lg shadow-lg cursor-pointer leading-none`};

  &:hover {
    ${tw`bg-white`};
  }
`;

interface HoverInfoType {
  areaName: string;
  fieldName: string;
  value: number;
  unit: string;
}

interface Props {
  geojson: MapstatGeoJsonType | null;
  colorMapping: ColorStopType[];
  minZoom: number;
  onBoundsChange: (bounds: MapstatBoundsType) => void;
  onSearchClick: () => void;
  onMapLoad?: (map: LeafletMap | null) => void;
}

interface BridgeProps {
  minZoom: number;
  onBoundsChange: (bounds: MapstatBoundsType) => void;
  onMapLoad: (map: LeafletMap | null) => void;
}

const MapBridge: FC<BridgeProps> = ({ minZoom, onBoundsChange, onMapLoad }) => {
  const map = useMap();

  const reportBounds = () => {
    const bounds = map.getBounds();
    onBoundsChange({
      minLat: bounds.getSouth(),
      maxLat: bounds.getNorth(),
      minLng: bounds.getWest(),
      maxLng: bounds.getEast(),
    });
  };

  useEffect(() => {
    onMapLoad(map);
    reportBounds();
    return () => onMapLoad(null);
  }, [map]);

  useEffect(() => {
    map.setMinZoom(minZoom);
  }, [map, minZoom]);

  useMapEvents({ moveend: reportBounds });

  return null;
};

const MapstatMap: FC<Props> = ({
  geojson,
  colorMapping,
  minZoom,
  onBoundsChange,
  onSearchClick,
  onMapLoad,
}) => {
  const [hoverInfo, setHoverInfo] = useState<HoverInfoType | null>(null);
  const [geojsonKey, setGeojsonKey] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const geoJsonRef = useRef<LeafletGeoJSON | null>(null);
  const colorMappingRef = useRef<ColorStopType[]>(colorMapping);
  colorMappingRef.current = colorMapping;

  // Remount the GeoJSON layer when new search results arrive
  useEffect(() => {
    setGeojsonKey((previous) => previous + 1);
  }, [geojson]);

  const styleFeature = (feature?: Feature): PathOptions => ({
    fillColor: getPolygonColor(
      Number(feature?.properties?.field_value),
      colorMappingRef.current
    ),
    fillOpacity: 0.55,
    color: '#1e293b',
    opacity: 0.6,
    weight: 1,
  });

  // Restyle in place when only the color scale changes
  useEffect(() => {
    geoJsonRef.current?.setStyle(styleFeature);
  }, [colorMapping]);

  const handleEachFeature = (feature: Feature, layer: Layer) => {
    layer.on({
      mouseover: (event) => {
        const target = event.target;
        target.setStyle({ weight: 3, opacity: 1 });
        target.bringToFront();

        const properties = feature.properties ?? {};
        setHoverInfo({
          areaName: String(properties.area_display_name ?? ''),
          fieldName: String(properties.field_display_name ?? ''),
          value: Number(properties.field_value),
          unit: String(properties.field_unit ?? ''),
        });
      },
      mouseout: (event) => {
        geoJsonRef.current?.resetStyle(event.target);
        setHoverInfo(null);
      },
    });
  };

  const handleMapLoad = (map: LeafletMap | null) => {
    mapRef.current = map;
    onMapLoad?.(map);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      wrapperRef.current?.requestFullscreen();
    }
  };

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
      // Leaflet needs a size recalculation after the container resizes
      setTimeout(() => mapRef.current?.invalidateSize(), 150);
    };

    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  return (
    <StyledMap ref={wrapperRef}>
      <MapContainer
        center={[DEFAULT_CENTER.lat, DEFAULT_CENTER.lng]}
        zoom={DEFAULT_ZOOM}
        minZoom={minZoom}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          crossOrigin="anonymous"
        />
        {geojson?.features?.length ? (
          <GeoJSON
            key={geojsonKey}
            ref={geoJsonRef}
            data={geojson as unknown as GeoJsonObject}
            style={styleFeature}
            onEachFeature={handleEachFeature}
          />
        ) : null}
        <MapBridge
          minZoom={minZoom}
          onBoundsChange={onBoundsChange}
          onMapLoad={handleMapLoad}
        />
      </MapContainer>
      <FullscreenButton
        type="button"
        title={isFullscreen ? 'Avsluta fullskärm' : 'Fullskärm'}
        onClick={toggleFullscreen}
      >
        {isFullscreen ? '✕' : '⛶'}
      </FullscreenButton>
      {hoverInfo && (
        <HoverCard>
          <HoverTitle>{hoverInfo.areaName}</HoverTitle>
          <HoverDivider />
          <HoverLabel>{hoverInfo.fieldName}</HoverLabel>
          <HoverValueRow>
            <HoverSwatch
              $color={
                Number.isFinite(hoverInfo.value)
                  ? getPolygonColor(hoverInfo.value, colorMapping)
                  : '#e2e8f0'
              }
            />
            <HoverValue>
              {Number.isFinite(hoverInfo.value)
                ? hoverInfo.value.toLocaleString('sv-SE')
                : '–'}
            </HoverValue>
            <HoverUnit>{hoverInfo.unit}</HoverUnit>
          </HoverValueRow>
        </HoverCard>
      )}
      {colorMapping.length > 0 && (
        <LegendCard>
          <LegendHeading>{strings.mapstat.legend}</LegendHeading>
          {colorMapping.map((stop, index) => (
            <LegendRow key={`${stop.threshold}-${index}`}>
              <LegendSwatch $color={stop.color} />
              <span>
                {Math.round(stop.threshold).toLocaleString('sv-SE')}
                {index === colorMapping.length - 1 ? ' +' : ''}
              </span>
            </LegendRow>
          ))}
        </LegendCard>
      )}
      <SearchButton type="button" onClick={onSearchClick}>
        {strings.mapstat.search}
      </SearchButton>
    </StyledMap>
  );
};

export default MapstatMap;
